import React from 'react'
import styled from '@emotion/styled'
import { PrivateLesson } from '../../../pure-js/types/GrooverTypes'
import { getInstructorName, getLocationName } from '../../../pure-js/libs/PrivateLessonHelpers'
import { formatGrooverText } from '../libs/GrooverTextRepository'

const OccasionsContainer = styled.div`
  width: 100%;
  min-width: 0;
`

const FilterHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid #e5e7eb;
`

const FilterTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: #111827;
  margin: 0;
`

const FilterOrderId = styled.div`
  font-size: 12px;
  color: #6b7280;
  font-family: monospace;
  margin-bottom: 0;
`

const BackToAllLink = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 0;
  background: none;
  border: none;
  color: var(--portal-primary, #7c3aed);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: color 0.2s;

  &:hover {
    color: var(--portal-primary-dark, #6d28d9);
    text-decoration: underline;
  }
`

const OccasionsList = styled.div`
  display: grid;
  gap: 20px;
  grid-template-columns: repeat(auto-fill, minmax(min(100%, 400px), 1fr));

  @media (min-width: 769px) {
    grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`

const OccasionCard = styled.div`
  padding: 20px;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  transition: all 0.2s;

  &:hover {
    border-color: var(--portal-primary, #7c3aed);
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(var(--portal-primary-rgb, 124, 58, 237), 0.15);
  }
`

const OccasionDate = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: var(--portal-primary, #7c3aed);
  margin-bottom: 8px;
`

const OccasionDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 12px;
`

const OccasionTitle = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #111827;
`

const OccasionInfo = styled.div`
  font-size: 14px;
  color: #6b7280;
`

const OccasionStatus = styled.div<{ status: string }>`
  display: inline-flex;
  align-items: center;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  text-transform: capitalize;
  margin-bottom: 12px;

  ${({ status }) => {
    switch (status) {
      case 'completed':
        return `
          background: rgba(34, 197, 94, 0.1);
          color: #22c55e;
        `
      case 'scheduled':
        return `
          background: rgba(59, 130, 246, 0.1);
          color: #3b82f6;
        `
      case 'requested':
        return `
          background: rgba(251, 191, 36, 0.1);
          color: #f59e0b;
        `
      case 'cancelled':
        return `
          background: rgba(239, 68, 68, 0.1);
          color: #ef4444;
        `
      default:
        return `
          background: rgba(156, 163, 175, 0.1);
          color: #9ca3af;
        `
    }
  }}
`

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 40vh;
  gap: 16px;
`

const EmptyIcon = styled.div`
  font-size: 64px;
  color: #6b7280;
`

const EmptyTitle = styled.h2`
  font-size: 24px;
  font-weight: 600;
  color: #111827;
  margin: 0;
`

const EmptyDescription = styled.p`
  font-size: 16px;
  color: #6b7280;
  text-align: center;
  max-width: 400px;
  margin: 0;
  line-height: 1.5;
`

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 40vh;
  gap: 16px;
`

const LoadingSpinner = styled.div`
  width: 48px;
  height: 48px;
  border: 4px solid rgba(var(--portal-primary-rgb, 124, 58, 237), 0.1);
  border-top-color: var(--portal-primary, #7c3aed);
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`

const LoadingText = styled.div`
  font-size: 16px;
  color: #6b7280;
`

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 40vh;
  gap: 16px;
`

const ErrorIcon = styled.div`
  font-size: 48px;
  color: #ef4444;
`

const ErrorText = styled.div`
  font-size: 16px;
  color: #ef4444;
  text-align: center;
  max-width: 400px;
`

// Helper functions
const formatDateTime = (dateString: string): string => {
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

const getDurationInMinutes = (startDate: string, endDate: string): number => {
  const start = new Date(startDate)
  const end = new Date(endDate)
  return Math.round((end.getTime() - start.getTime()) / (1000 * 60))
}

const getStatusLabel = (status: string): string => {
  const statusLabels: Record<string, string> = {
    scheduled: 'Scheduled',
    completed: 'Completed',
    requested: 'Requested',
    cancelled: 'Cancelled'
  }
  return statusLabels[status] || status
}

export type OccasionsFilterOrder = {
  id: string
  packageName?: string
}

interface OccasionsTabProps {
  occasions: PrivateLesson[] | undefined
  loading: boolean
  error: Error | undefined
  filterOrder?: OccasionsFilterOrder | null
  onBackToAllOccasions?: () => void
  getText?: (key: string) => string
}

export const OccasionsTab: React.FC<OccasionsTabProps> = ({
  occasions,
  loading,
  error,
  filterOrder,
  onBackToAllOccasions,
  getText = (k: string) => k
}) => {
  if (loading) {
    return (
      <LoadingContainer>
        <LoadingSpinner />
        <LoadingText>Loading your occasions...</LoadingText>
      </LoadingContainer>
    )
  }

  if (error) {
    return (
      <ErrorContainer>
        <ErrorIcon>⚠️</ErrorIcon>
        <ErrorText>Failed to load your occasions. Please try again.</ErrorText>
      </ErrorContainer>
    )
  }

  const filteredOccasions = filterOrder && occasions ? occasions.filter((o) => o.orderId === filterOrder.id) : occasions

  if (!filteredOccasions || filteredOccasions.length === 0) {
    return (
      <OccasionsContainer>
        {filterOrder && (
          <FilterHeader>
            <FilterTitle>
              {formatGrooverText(getText('portalOccasionsForOrder'), [
                filterOrder.packageName || getText('portalPrivateLessonPackage')
              ])}
            </FilterTitle>
            <FilterOrderId>
              {getText('portalOrderId')}: {filterOrder.id}
            </FilterOrderId>
            {onBackToAllOccasions && (
              <BackToAllLink onClick={onBackToAllOccasions}>{getText('portalBackToAllOccasions')}</BackToAllLink>
            )}
          </FilterHeader>
        )}
        <EmptyState>
          <EmptyIcon>📅</EmptyIcon>
          <EmptyTitle>No occasions found</EmptyTitle>
          <EmptyDescription>
            {filterOrder
              ? getText('portalNoOccasionsForOrder')
              : "You don't have any scheduled private lessons yet. Contact us to schedule your first lesson!"}
          </EmptyDescription>
        </EmptyState>
      </OccasionsContainer>
    )
  }

  return (
    <OccasionsContainer>
      {filterOrder && (
        <FilterHeader>
          <FilterTitle>
            {formatGrooverText(getText('portalOccasionsForOrder'), [
              filterOrder.packageName || getText('portalPrivateLessonPackage')
            ])}
          </FilterTitle>
          <FilterOrderId>
            {getText('portalOrderId')}: {filterOrder.id}
          </FilterOrderId>
          {onBackToAllOccasions && (
            <BackToAllLink onClick={onBackToAllOccasions}>{getText('portalBackToAllOccasions')}</BackToAllLink>
          )}
        </FilterHeader>
      )}
      <OccasionsList>
        {filteredOccasions.map((occasion) => {
          const instructorName = getInstructorName(occasion) || 'TBD'
          const locationName = getLocationName(occasion) || 'TBD'
          const duration = getDurationInMinutes(occasion.startDate, occasion.endDate)

          return (
            <OccasionCard key={occasion.id}>
              <OccasionDate>{formatDateTime(occasion.startDate)}</OccasionDate>
              <OccasionDetails>
                <OccasionTitle>Private Lesson</OccasionTitle>
                <OccasionInfo>Instructor: {instructorName}</OccasionInfo>
                <OccasionInfo>Location: {locationName}</OccasionInfo>
                <OccasionInfo>Duration: {duration} minutes</OccasionInfo>
                {occasion.attended && <OccasionInfo>✓ Attended</OccasionInfo>}
              </OccasionDetails>
              <OccasionStatus status={occasion.status}>{getStatusLabel(occasion.status)}</OccasionStatus>
            </OccasionCard>
          )
        })}
      </OccasionsList>
    </OccasionsContainer>
  )
}
